import { useMemo } from 'react';
import { Pill } from 'ui-kit/Pill';
import type { GetDealRemittanceSummaries_getDealRemittanceSummaries_summaries_by_payment_month } from './__generated__/GetDealRemittanceSummaries';
import { formatPaymentMonth, toNum } from './utils';

type SummaryMonth =
  GetDealRemittanceSummaries_getDealRemittanceSummaries_summaries_by_payment_month;

function formatDollars(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

type Metric = { label: string; value: string };

function buildMetricsFromLatestMonth(latest: SummaryMonth): Metric[] {
  const s = latest.summary;
  const metrics: Metric[] = [
    {
      label: 'Month',
      value: formatPaymentMonth(latest.payment_month) ?? latest.payment_month,
    },
  ];
  if (!s) return metrics;
  // Strip shows the traded pool at 100% tape (same as the chart below). Per-party
  // numbers (Investor/Servicer shares) live in the sidecar. Fees are always full
  // (servicer keeps 100%); a "Your share: X%" annotation on the page header carries
  // the participation context.
  return [
    ...metrics,
    { label: 'UPB', value: formatDollars(toNum(s.ending_balance_cents)) },
    { label: 'Loans', value: formatCount(toNum(s.loan_count)) },
    { label: 'Principal', value: formatDollars(toNum(s.total_principal_full_cents)) },
    { label: 'Interest', value: formatDollars(toNum(s.total_interest_full_cents)) },
    { label: 'Payments', value: formatDollars(toNum(s.total_payments_full_cents)) },
    { label: 'Fees', value: formatDollars(toNum(s.total_fees_cents)) },
    { label: 'Delinquent', value: formatCount(toNum(s.delinquent_count)) },
    { label: 'Prepayments', value: formatCount(toNum(s.prepayment_count)) },
  ];
}

type RemittancePerformanceSummaryProps = {
  summariesByPaymentMonth: SummaryMonth[];
  loading?: boolean;
  /** Explicit payment month (YYYY-MM) shown in the Month pill when summaries lack one. */
  paymentMonth?: string | null;
};

export const RemittancePerformanceSummary = ({
  summariesByPaymentMonth,
  loading,
  paymentMonth: paymentMonthProp,
}: RemittancePerformanceSummaryProps) => {
  const metrics = useMemo(() => {
    if (!summariesByPaymentMonth.length) {
      if (paymentMonthProp) {
        return [
          {
            label: 'Month',
            value: formatPaymentMonth(paymentMonthProp) ?? paymentMonthProp,
          },
        ];
      }
      return [];
    }
    const sorted = [...summariesByPaymentMonth].sort((a, b) =>
      a.payment_month.localeCompare(b.payment_month),
    );
    const latest = sorted[sorted.length - 1]!;
    const monthValue = latest.payment_month || paymentMonthProp;
    const result = buildMetricsFromLatestMonth(latest);
    if (monthValue && result.length > 0 && !result[0]?.value) {
      result[0] = {
        label: 'Month',
        value: formatPaymentMonth(monthValue) ?? monthValue,
      };
    }
    return result;
  }, [summariesByPaymentMonth, paymentMonthProp]);

  if (loading) {
    return (
      <div className="flex flex-row flex-wrap items-center gap-2 pt-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Pill isLoading key={i} />
        ))}
      </div>
    );
  }

  if (!metrics.length) return null;

  return (
    <div className="flex flex-row flex-wrap items-center gap-2 pt-1">
      {metrics.map((m) => (
        <Pill key={m.label} label={m.label} value={m.value} />
      ))}
    </div>
  );
};
